import React from 'react'
import Axios from 'axios'
import Modal from 'react-responsive-modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import AppComponent from 'components/common/AppComponent'
import ReactSelect from 'components/common/ReactSelect'
import ChatCustomVariablesSelector from 'components/chatflows/ChatCustomVariablesSelector'
import ChatVariablesSelector from 'components/chatflows/ChatVariablesSelector'
import { ChatItemType } from 'constants/ChatItemType'
import { API } from 'api/APIClient'
import UUID from 'utils/UUID'

export default class ChatConnectorForm extends AppComponent {

    static normalizeRequestMappings(mappings) {
        if (!(mappings instanceof Array)) {
            return [];
        }

        return mappings.map((mapping) => {
            let customProperty = null;
            if (mapping && typeof mapping.customProperty === 'string') {
                customProperty = mapping.customProperty;
            }
            else if (mapping && mapping.customProperty && mapping.customProperty.key) {
                customProperty = mapping.customProperty.key;
            }

            return {
                customProperty,
                propertyName: mapping && (mapping.propertyName || mapping.name) ? (mapping.propertyName || mapping.name) : '',
                dataType: mapping && mapping.dataType ? mapping.dataType : 'STRING'
            }
        });
    }

    static normalizeResponseMappings(mappings) {
        if (!(mappings instanceof Array)) {
            return [];
        }

        return mappings.map((mapping) => {
            let customProperty = null;
            if (mapping && typeof mapping.customProperty === 'string') {
                customProperty = mapping.customProperty;
            }
            else if (mapping && mapping.customProperty && mapping.customProperty.key) {
                customProperty = mapping.customProperty.key;
            }

            return {
                customProperty,
                propertyName: mapping && (mapping.propertyName || mapping.name) ? (mapping.propertyName || mapping.name) : ''
            }
        });
    }

    constructor(props, context) {
        super(props);

        const source = props.node || {};
        const connectorID = source.connectorID || (source.connector && source.connector.id) || null;
        const contextOrganisation = (context && context.organisation) ? context.organisation : { id: null, name: '' };

        const item = Object.assign({
            typeText: 'Connector Request',
            typeID: ChatItemType.CONNECTOR,
            uuid: UUID.uuidv4(),
            organisationID: source.organisationID || contextOrganisation.id,
            organisationName: source.organisationName || contextOrganisation.name,
            connectorID,
            endpoint: '',
            method: 'POST',
            failGracefully: 0,
            failCustomProperty: null,
            failValue: '',
            requestMappings: [],
            responseMappings: [],
            nodeText: null
        }, source);

        if (item.failCustomProperty && typeof item.failCustomProperty === 'string') {
            item.failCustomProperty = {
                key: item.failCustomProperty
            };
        }

        item.requestMappings = ChatConnectorForm.normalizeRequestMappings(source.requestMappings);
        item.responseMappings = ChatConnectorForm.normalizeResponseMappings(source.responseMappings);

        this.state = {
            item,
            validated: false,
            errors: {},
            touched: {},
            isLoadingConnectors: false,
            organisationOptions: [
                {
                    value: item.organisationID,
                    label: item.organisationName || contextOrganisation.name
                }
            ],
            connectorOptions: [],
            methodOptions: [
                { value: 'POST', label: 'POST' },
                { value: 'GET', label: 'GET' },
                { value: 'DELETE', label: 'DELETE' },
                { value: 'PUT', label: 'PUT' },
                { value: 'PATCH', label: 'PATCH' }
            ],
            dataTypeOptions: [
                { value: 'STRING', label: 'STRING' },
                { value: 'INT', label: 'INT' },
                { value: 'BOOL', label: 'BOOL' }
            ],
            modal: {
                isOpen: false,
                content: null,
                size: 'modal-large'
            }
        };
    }

    componentDidMount() {
        this.validate();

        const permissions = (this.context && this.context.permissions) ? this.context.permissions : {};
        if (permissions.connector && permissions.connector.allOrgs) {
            this.getOrganisationOptions();
        }

        this.fetchConnectorOptions();
    }

    getOrganisationOptions = () => {
        Axios
            .get(API.organisation.getOrganisations, {
                props: {
                    appUsersOnly: 1
                }
            })
            .then((res) => {
                if (res.data.success) {
                    this.setState({
                        organisationOptions: res.data.data.organisations.map((organisation) => {
                            return {
                                value: organisation.id.toString(),
                                label: organisation.name
                            }
                        })
                    })
                }
            })
    }

    fetchConnectorOptions = () => {
        if (!this.state.item.organisationID || this.state.isLoadingConnectors) {
            if (!this.state.item.organisationID) {
                this.setState({ connectorOptions: [] });
            }
            return;
        }

        this.setState({
            isLoadingConnectors: true
        }, () => {
            Axios
                .get(API.connector.getConnectors || 'connector/getConnectors', {
                    params: {
                        organisationID: this.state.item.organisationID
                    }
                })
                .then((res) => {
                    if (res.data.success) {
                        const data = res.data.data;
                        const connectors =
                            (data && data.connectors) ||
                            (Array.isArray(data) ? data : []);

                        this.setState({
                            connectorOptions: connectors.map((connector) => {
                                const typeName = connector.typeName ? ` (${connector.typeName})` : '';
                                return {
                                    value: connector.id,
                                    label: `${connector.name || 'Unnamed'}${typeName}`
                                }
                            }),
                            isLoadingConnectors: false
                        }, this.validate)
                    }
                    else {
                        this.setState({
                            connectorOptions: [],
                            isLoadingConnectors: false
                        }, this.validate)
                    }
                })
                .catch(() => {
                    this.setState({
                        connectorOptions: [],
                        isLoadingConnectors: false
                    }, this.validate)
                })
        })
    }

    getConnectorLabel = (connectorID) => {
        const selected = this.state.connectorOptions.find((option) => {
            if (option.value === null || option.value === undefined || connectorID === null || connectorID === undefined) {
                return false;
            }
            return option.value.toString() === connectorID.toString();
        });

        return selected ? selected.label : '';
    }

    updateNodeText = (item) => {
        const method = item.method || 'POST';
        const endpoint = item.endpoint || '';
        const connectorLabel = this.getConnectorLabel(item.connectorID);

        if (connectorLabel && endpoint) {
            item.nodeText = `${connectorLabel} - ${method} ${endpoint}`;
        }
        else if (endpoint) {
            item.nodeText = `${method} ${endpoint}`;
        }
        else if (connectorLabel) {
            item.nodeText = connectorLabel;
        }
        else {
            item.nodeText = 'Connector Request';
        }
    }

    handleBlur = (event) => {
        const touched = Object.assign({}, this.state.touched, {
            [event.target.name]: true
        });

        this.setState({
            touched
        }, this.validate)
    }

    markMappingTouched = (key) => {
        const touched = Object.assign({}, this.state.touched, {
            [key]: true
        });

        this.setState({
            touched
        }, this.validate)
    }

    handleChange = (event) => {
        const target = event.target;
        const name = target.name;
        const value = target.type === 'checkbox' ? (target.checked ? 1 : 0) : target.value;

        const item = Object.assign({}, this.state.item);
        item[name] = value;

        if (name === 'organisationID') {
            item.connectorID = null;
        }
        else if (name === 'failGracefully' && value === 0) {
            item.failCustomProperty = null;
            item.failValue = '';
        }

        this.updateNodeText(item);

        this.setState({
            item,
            connectorOptions: name === 'organisationID' ? [] : this.state.connectorOptions
        }, () => {
            this.validate();
            if (name === 'organisationID') {
                this.fetchConnectorOptions();
            }
        })
    }

    handleMappingChange = (listKey, index, key, value) => {
        const item = Object.assign({}, this.state.item);
        const mappings = Array.from(item[listKey] || []);

        if (!mappings[index]) {
            return;
        }

        mappings[index] = Object.assign({}, mappings[index], {
            [key]: value
        });
        item[listKey] = mappings;

        this.setState({
            item
        }, this.validate)
    }

    addRequestMapping = () => {
        const item = Object.assign({}, this.state.item);
        const requestMappings = Array.from(item.requestMappings || []);

        requestMappings.push({
            customProperty: null,
            propertyName: '',
            dataType: 'STRING'
        });

        item.requestMappings = requestMappings;

        this.setState({
            item
        }, this.validate)
    }

    removeRequestMapping = (index) => {
        const item = Object.assign({}, this.state.item);
        const requestMappings = Array.from(item.requestMappings || []);

        requestMappings.splice(index, 1);
        item.requestMappings = requestMappings;

        this.setState({
            item
        }, this.validate)
    }

    addResponseMapping = () => {
        const item = Object.assign({}, this.state.item);
        const responseMappings = Array.from(item.responseMappings || []);

        responseMappings.push({
            customProperty: null,
            propertyName: ''
        });

        item.responseMappings = responseMappings;

        this.setState({
            item
        }, this.validate)
    }

    removeResponseMapping = (index) => {
        const item = Object.assign({}, this.state.item);
        const responseMappings = Array.from(item.responseMappings || []);

        responseMappings.splice(index, 1);
        item.responseMappings = responseMappings;

        this.setState({
            item
        }, this.validate)
    }

    openCustomPropertySelector = (listKey, index) => {
        this.setState({
            modal: {
                isOpen: true,
                size: 'modal-large',
                content: (
                    <ChatCustomVariablesSelector
                        showSelectButton
                        showCopyButton={false}
                        onSelected={(property) => {
                            this.handleMappingChange(listKey, index, 'customProperty', property ? property.key : null);
                            this.closeModal();
                        }}
                        onCancel={this.closeModal}
                    />
                )
            }
        })
    }

    showFailCustomPropertySelector = () => {
        if (this.state.modal.isOpen) {
            return;
        }

        this.setState({
            modal: {
                isOpen: true,
                size: 'modal-medium',
                content: (
                    <ChatCustomVariablesSelector
                        showCopyButton={false}
                        showSelectButton={true}
                        onSelected={(variable) => {
                            const item = Object.assign({}, this.state.item);
                            item.failCustomProperty = variable || null;
                            this.setState({
                                item
                            }, () => {
                                this.closeModal();
                                this.validate();
                            })
                        }}
                        onCancel={() => this.closeModal()}
                    />
                )
            }
        })
    }

    showVariablesSelector = () => {
        if (this.state.modal.isOpen) {
            return;
        }

        this.setState({
            modal: {
                isOpen: true,
                size: 'modal-medium',
                content: (
                    <ChatVariablesSelector
                        showCopyButton={true}
                        showSelectButton={false}
                        showCondVariables={false}
                        showTextVariables={true}
                        onSelected={() => this.closeModal()}
                        onCancel={() => this.closeModal()}
                    />
                )
            }
        })
    }

    showCustomVariablesSelectorForEndpoint = () => {
        if (this.state.modal.isOpen) {
            return;
        }

        this.setState({
            modal: {
                isOpen: true,
                size: 'modal-medium',
                content: (
                    <ChatCustomVariablesSelector
                        showCopyButton={true}
                        showSelectButton={false}
                        showCondVariables={false}
                        showTextVariables={true}
                        onSelected={() => this.closeModal()}
                        onCancel={() => this.closeModal()}
                    />
                )
            }
        })
    }

    closeModal = () => {
        this.setState({
            modal: {
                isOpen: false,
                content: null,
                size: 'modal-large'
            }
        })
    }

    validate = () => {
        let validated = true;
        const errors = {};

        const item = this.state.item;
        const touched = this.state.touched;

        if (!item.organisationID) {
            validated = false;
            if (touched.organisationID) {
                errors.organisationID = 'Organisation is required';
            }
        }

        if (!item.connectorID) {
            validated = false;
            if (touched.connectorID) {
                errors.connectorID = 'Connector is required';
            }
        }

        if (!item.endpoint || !item.endpoint.trim()) {
            validated = false;
            if (touched.endpoint) {
                errors.endpoint = 'Endpoint is required';
            }
        }

        (item.requestMappings || []).forEach((mapping, index) => {
            if (!mapping.customProperty || !mapping.propertyName) {
                validated = false;
                if (touched[`requestMappings_${index}`]) {
                    errors[`requestMappings_${index}`] = 'Custom Property and Property Name are required';
                }
            }
        });

        (item.responseMappings || []).forEach((mapping, index) => {
            if (!mapping.customProperty || !mapping.propertyName) {
                validated = false;
                if (touched[`responseMappings_${index}`]) {
                    errors[`responseMappings_${index}`] = 'Property Name and Custom Property are required';
                }
            }
        });

        this.setState({
            validated,
            errors
        })
    }

    handleSubmit = (event) => {
        event.preventDefault();

        if (this.props.onSubmitted instanceof Function) {
            const item = Object.assign({}, this.state.item);
            item.endpoint = (item.endpoint || '').trim();
            this.updateNodeText(item);
            this.props.onSubmitted(item);
        }
    }

    renderRequestMappings = () => {
        return (
            <fieldset className="fieldset">
                <legend>Request mappings</legend>

                <div className="grid-x grid-padding-x">
                    <div className="small-12 cell" style={{marginBottom: 10}}>
                        <button className="button small" type="button" onClick={this.addRequestMapping}>
                            Add Request Mapping
                        </button>
                    </div>
                </div>

                {(this.state.item.requestMappings || []).map((mapping, index) => {
                    return (
                        <div className="grid-x grid-padding-x align-bottom" key={`request-mapping-${index}`}>
                            <div className="small-12 medium-4 cell">
                                <span style={{fontSize: 12}}>Custom Property</span>
                                <div className="input-group">
                                    <input className="input-group-field" type="text" value={mapping.customProperty || 'Not Selected'} disabled />
                                    <div className="input-group-button">
                                        <button type="button" className="button" onClick={() => this.openCustomPropertySelector('requestMappings', index)}>
                                            Select
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="small-12 medium-4 cell">
                                <span style={{fontSize: 12}}>Property Name</span>
                                <input
                                    type="text"
                                    value={mapping.propertyName}
                                    onChange={(e) => this.handleMappingChange('requestMappings', index, 'propertyName', e.target.value)}
                                    onBlur={() => this.markMappingTouched(`requestMappings_${index}`)}
                                />
                            </div>

                            <div className="small-12 medium-3 cell">
                                <span style={{fontSize: 12}}>Datatype</span>
                                <ReactSelect
                                    name={`requestMappings_dataType_${index}`}
                                    className="react-select"
                                    onChange={(e) => this.handleMappingChange('requestMappings', index, 'dataType', e.target.value)}
                                    value={mapping.dataType || 'STRING'}
                                    options={this.state.dataTypeOptions}
                                />
                            </div>

                            <div className="small-12 medium-shrink cell" style={{display: 'flex', alignItems: 'flex-end'}}>
                                <button type="button" className="button alert tiny" style={{width: 32, height: 32, padding: 0}} onClick={() => this.removeRequestMapping(index)}>
                                    <FontAwesomeIcon icon="trash" />
                                </button>
                            </div>

                            {this.state.errors[`requestMappings_${index}`] &&
                            <div className="small-12 cell">
                                <small className="error">{this.state.errors[`requestMappings_${index}`]}</small>
                            </div>
                            }
                        </div>
                    )
                })}
            </fieldset>
        )
    }

    renderResponseMappings = () => {
        return (
            <fieldset className="fieldset">
                <legend>Response mappings</legend>

                <div className="grid-x grid-padding-x">
                    <div className="small-12 cell" style={{marginBottom: 10}}>
                        <button className="button small" type="button" onClick={this.addResponseMapping}>
                            Add Response Mapping
                        </button>
                    </div>
                </div>

                {(this.state.item.responseMappings || []).map((mapping, index) => {
                    return (
                        <div className="grid-x grid-padding-x align-bottom" key={`response-mapping-${index}`}>
                            <div className="small-12 medium-5 cell">
                                <span style={{fontSize: 12}}>Property Name</span>
                                <input
                                    type="text"
                                    value={mapping.propertyName}
                                    onChange={(e) => this.handleMappingChange('responseMappings', index, 'propertyName', e.target.value)}
                                    onBlur={() => this.markMappingTouched(`responseMappings_${index}`)}
                                />
                            </div>

                            <div className="small-12 medium-6 cell">
                                <span style={{fontSize: 12}}>Custom Property</span>
                                <div className="input-group">
                                    <input className="input-group-field" type="text" value={mapping.customProperty || 'Not Selected'} disabled />
                                    <div className="input-group-button">
                                        <button type="button" className="button" onClick={() => this.openCustomPropertySelector('responseMappings', index)}>
                                            Select
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="small-12 medium-shrink cell" style={{display: 'flex', alignItems: 'flex-end'}}>
                                <button type="button" className="button alert tiny" style={{width: 32, height: 32, padding: 0}} onClick={() => this.removeResponseMapping(index)}>
                                    <FontAwesomeIcon icon="trash" />
                                </button>
                            </div>

                            {this.state.errors[`responseMappings_${index}`] &&
                            <div className="small-12 cell">
                                <small className="error">{this.state.errors[`responseMappings_${index}`]}</small>
                            </div>
                            }
                        </div>
                    )
                })}
            </fieldset>
        )
    }

    render() {
        return (
            <React.Fragment>
                <div className="modal-head">
                    <h3>Chat connector</h3>
                </div>

                <form onSubmit={this.handleSubmit}>
                    <div className="modal-body">
                        <div className="grid-x grid-padding-x">
                            <div className="small-3 cell">
                                <label htmlFor="organisationID" className="text-right middle">* Organisation</label>
                            </div>
                            <div className={"small-9 cell " + (this.state.errors.organisationID && 'error')}>
                                <ReactSelect
                                    name="organisationID"
                                    className="react-select"
                                    onChange={this.handleChange}
                                    value={this.state.item.organisationID}
                                    options={this.state.organisationOptions}
                                    onBlur={this.handleBlur}
                                />
                                <small className="error">{this.state.errors.organisationID}</small>
                            </div>
                        </div>

                        <div className="grid-x grid-padding-x">
                            <div className="small-3 cell">
                                <label htmlFor="connectorID" className="text-right middle">* Connector</label>
                            </div>
                            <div className={"small-9 cell " + (this.state.errors.connectorID && 'error')}>
                                <ReactSelect
                                    name="connectorID"
                                    className="react-select"
                                    onChange={this.handleChange}
                                    value={this.state.item.connectorID}
                                    options={this.state.connectorOptions}
                                    onBlur={this.handleBlur}
                                    isDisabled={!this.state.item.organisationID || this.state.isLoadingConnectors}
                                />
                                <small className="error">{this.state.errors.connectorID}</small>
                            </div>
                        </div>

                        <div className="grid-x grid-padding-x">
                            <div className="small-3 cell">
                                <label htmlFor="endpoint" className="text-right middle">* Endpoint</label>
                            </div>
                            <div className={"small-9 cell " + (this.state.errors.endpoint && 'error')}>
                                <input name="endpoint" type="text" value={this.state.item.endpoint || ''} onChange={this.handleChange} onBlur={this.handleBlur} />
                                <small className="field-info">You can inject System Properties and Custom Properties into the Endpoint URL.</small>
                                <small className="error">{this.state.errors.endpoint}</small>
                            </div>
                        </div>

                        <div className="grid-x grid-padding-x">
                            <div className="small-3 cell">
                                <label htmlFor="endpointSystemProperties" className="text-right middle">System properties</label>
                            </div>
                            <div className="small-9 cell">
                                <button className="button primary" type="button" onClick={this.showVariablesSelector}>
                                    Select system properties
                                </button>
                            </div>
                        </div>

                        <div className="grid-x grid-padding-x">
                            <div className="small-3 cell">
                                <label htmlFor="endpointCustomProperties" className="text-right middle">Custom properties</label>
                            </div>
                            <div className="small-9 cell">
                                <button className="button primary" type="button" onClick={this.showCustomVariablesSelectorForEndpoint}>
                                    Select custom properties
                                </button>
                            </div>
                        </div>

                        <div className="grid-x grid-padding-x">
                            <div className="small-3 cell">
                                <label htmlFor="method" className="text-right middle">Method</label>
                            </div>
                            <div className="small-9 cell">
                                <ReactSelect
                                    name="method"
                                    className="react-select"
                                    onChange={this.handleChange}
                                    value={this.state.item.method || 'POST'}
                                    options={this.state.methodOptions}
                                />
                            </div>
                        </div>

                        <div className="grid-x grid-padding-x">
                            <div className="small-3 cell">
                                <label htmlFor="failGracefully" className="text-right middle">Fail Gracefully</label>
                            </div>
                            <div className="small-9 cell">
                                <label className="chkbx-container">
                                    <input
                                        className="chkbx"
                                        type="checkbox"
                                        name="failGracefully"
                                        onChange={this.handleChange}
                                        checked={this.state.item.failGracefully === 1}
                                    />
                                    <span className="chkmk"></span>
                                </label>
                            </div>
                        </div>

                        {this.state.item.failGracefully === 1 &&
                        <div className="grid-x grid-padding-x align-bottom">
                            <div className="small-3 cell">
                                <label htmlFor="failCustomProperty" className="text-right middle">Custom Property</label>
                            </div>
                            <div className="small-4 cell">
                                <div className="input-group">
                                    <input
                                        className="input-group-field"
                                        type="text"
                                        value={this.state.item.failCustomProperty && this.state.item.failCustomProperty.key ? this.state.item.failCustomProperty.key : 'Not Selected'}
                                        disabled
                                    />
                                    <div className="input-group-button">
                                        <button type="button" className="button" onClick={this.showFailCustomPropertySelector}>
                                            Select
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="small-2 cell">
                                <label htmlFor="failValue" className="text-right middle">Set with value</label>
                            </div>
                            <div className="small-3 cell">
                                <input
                                    type="text"
                                    name="failValue"
                                    value={this.state.item.failValue || ''}
                                    onChange={this.handleChange}
                                />
                            </div>
                        </div>
                        }

                        <div className="grid-x grid-padding-x">
                            <div className="small-12 cell">
                                {this.renderRequestMappings()}
                            </div>
                        </div>

                        <div className="grid-x grid-padding-x">
                            <div className="small-12 cell">
                                {this.renderResponseMappings()}
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <div className="grid-x grid-padding-x">
                            <div className="small-12 cell">
                                <div className="btn-wrap">
                                    <button className="button secondary" type="button" onClick={this.props.onCancel}>
                                        Cancel
                                    </button>
                                    <button className="button success" type="submit" disabled={!this.state.validated}>
                                        Save
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>

                <Modal
                    classNames={{modal: this.state.modal.size}}
                    open={this.state.modal.isOpen}
                    onClose={this.closeModal}
                    center
                >
                    {this.state.modal.content}
                </Modal>
            </React.Fragment>
        )
    }
}
